// Location mapping utilities for determining location hierarchies
// This helps match user location searches to job location requirements
// Now powered by country-state-city npm package for comprehensive world coverage
// Cities are curated by population tiers to ensure relevant, stable search results

import { Country, State } from 'country-state-city'
import type { ICountry, IState } from 'country-state-city'
import { CURATED_CITIES } from './curatedCities'

export type LocationType = 'city' | 'state' | 'country'

export interface LocationInfo {
    name: string
    type: LocationType
    country: string
    countryCode?: string
    state?: string
    stateCode?: string
    latitude?: string
    longitude?: string
}

/**
 * Minor territories and locations to deprioritize in search results
 * These only appear when the search query specifically matches them (3+ chars)
 */
const DEPRIORITIZED_LOCATIONS = [
    'united states minor outlying islands',
    'united states virgin islands',
    'british virgin islands',
    'u.s. virgin islands',
    'heard island and mcdonald islands',
    'french southern territories',
    'south georgia and the south sandwich islands',
    'cocos (keeling) islands',
    'christmas island',
    'bouvet island',
    'norfolk island',
    'pitcairn',
    'tokelau',
    'western sahara',
    'svalbard and jan mayen'
]

/**
 * Country priority tiers for geographic bias in search results
 * Higher scores = more likely to be what users are searching for
 */
const COUNTRY_PRIORITY: Record<string, number> = {
    // Tier 1: Primary market (US)
    'US': 30,
    // Tier 2: Major markets
    'CA': 20, 'GB': 20, 'AU': 20, 'IE': 20, 'NZ': 20,
    'PT': 20, 'BR': 20, 'FR': 20, 'ES': 20, 'NL': 20, 'DE': 20,
    // Tier 3: Other major European/APAC markets
    'IT': 10, 'SE': 10, 'NO': 10, 'DK': 10, 'FI': 10,
    'CH': 10, 'AT': 10, 'BE': 10, 'PL': 10, 'CZ': 10,
    'JP': 10, 'KR': 10, 'SG': 10, 'HK': 10, 'IN': 10,
    'MX': 10, 'AR': 10, 'CL': 10, 'CO': 10
}

/**
 * Check if a location should be deprioritized
 */
function isDeprioritized(locationName: string): boolean {
    const normalized = locationName.toLowerCase()
    // Only exact matches should be deprioritized, not substring matches
    // This prevents "United States" from being penalized due to "United States Minor Outlying Islands"
    return DEPRIORITIZED_LOCATIONS.some(dep => normalized === dep)
}

/**
 * Get all countries from the package
 */
export function getAllCountries(): ICountry[] {
    return Country.getAllCountries()
}

/**
 * Get all states for a country
 */
export function getStatesOfCountry(countryCode: string): IState[] {
    return State.getStatesOfCountry(countryCode)
}

/**
 * Get all cities for a country (or state if stateCode provided)
 * Returns curated cities filtered by population tiers
 */
export function getCitiesOfCountry(countryCode: string, stateCode?: string): LocationInfo[] {
    let cities = CURATED_CITIES.filter(c => c.countryCode === countryCode)

    if (stateCode) {
        cities = cities.filter(c => c.stateCode === stateCode)
    }

    const countries = Country.getAllCountries()
    const country = countries.find(c => c.isoCode === countryCode)

    // Only show state/province for US and Canada
    const shouldShowState = countryCode === 'US' || countryCode === 'CA'

    return cities.map(city => ({
        name: city.name,
        type: 'city' as const,
        country: country?.name || countryCode,
        countryCode: city.countryCode,
        state: shouldShowState && city.stateCode ? State.getStateByCodeAndCountry(city.stateCode, countryCode)?.name : undefined,
        stateCode: shouldShowState ? city.stateCode || undefined : undefined,
        latitude: city.latitude,
        longitude: city.longitude
    }))
}

/**
 * Search for a location by name across cities, states, and countries
 */
export function searchLocations(query: string, limit: number = 10): LocationInfo[] {
    const normalizedQuery = query.toLowerCase().trim()
    const results: LocationInfo[] = []

    if (normalizedQuery.length < 2) {
        return results
    }

    // Search countries
    const countries = Country.getAllCountries()
    for (const country of countries) {
        if (country.name.toLowerCase().includes(normalizedQuery)) {
            // Skip deprioritized locations for short queries
            if (isDeprioritized(country.name) && normalizedQuery.length < 8) {
                continue
            }

            results.push({
                name: country.name,
                type: 'country',
                country: country.name,
                countryCode: country.isoCode,
                latitude: country.latitude,
                longitude: country.longitude
            })
        }
    }

    // Search states (only for US and Canada)
    const stateCountries = ['US', 'CA']
    for (const countryCode of stateCountries) {
        const states = State.getStatesOfCountry(countryCode)
        for (const state of states) {
            if (state.name.toLowerCase().includes(normalizedQuery)) {
                // Skip deprioritized territories for short queries
                if (isDeprioritized(state.name) && normalizedQuery.length < 8) {
                    continue
                }

                const country = countries.find(c => c.isoCode === countryCode)
                results.push({
                    name: state.name,
                    type: 'state',
                    country: country?.name || countryCode,
                    countryCode: countryCode,
                    state: state.name,
                    stateCode: state.isoCode,
                    latitude: state.latitude || undefined,
                    longitude: state.longitude || undefined
                })
            }
        }
    }

    // Search curated cities - population-filtered for stable, predictable results
    // This ensures search results stay consistent as users type (e.g., "uni" → "unit" → "unite")
    for (const city of CURATED_CITIES) {
        const cityNameLower = city.name.toLowerCase()
        if (cityNameLower.includes(normalizedQuery)) {
            const country = countries.find(c => c.isoCode === city.countryCode)

            // Only show state/province for US and Canada
            const shouldShowState = city.countryCode === 'US' || city.countryCode === 'CA'
            const state = shouldShowState && city.stateCode
                ? State.getStateByCodeAndCountry(city.stateCode, city.countryCode)
                : null

            results.push({
                name: city.name,
                type: 'city' as const,
                country: country?.name || city.countryCode,
                countryCode: city.countryCode,
                state: state?.name || undefined,
                stateCode: shouldShowState ? city.stateCode || undefined : undefined,
                latitude: city.latitude,
                longitude: city.longitude
            })
        }
    }

    // Sort by relevance using a scoring system
    return results
        .map(location => {
            const locationName = location.name.toLowerCase()
            let score = 0

            // Exact match gets highest priority
            if (locationName === normalizedQuery) {
                score += 100
            }
            // Starts with query
            else if (locationName.startsWith(normalizedQuery)) {
                score += 50
            }
            // First word starts with query (treat same as startsWith for better UX)
            else if (locationName.split(' ')[0].startsWith(normalizedQuery)) {
                score += 50
            }
            // Word boundary match (query matches start of any non-first word)
            else if (locationName.split(' ').slice(1).some(word => word.startsWith(normalizedQuery))) {
                score += 25
            }
            // Contains query anywhere
            else if (locationName.includes(normalizedQuery)) {
                score += 10
            }

            // Type bonuses - prioritize states and countries over cities
            // For short queries, boost countries even more to ensure they appear first
            if (location.type === 'country') {
                score += normalizedQuery.length < 5 ? 25 : 15
            }
            else if (location.type === 'state') {
                score += 12
            }
            else if (location.type === 'city') {
                // Cities in curated list get base boost (population-weighted via tiers)
                score += 10
            }

            // Geographic priority - boost results from key markets
            if (location.countryCode) {
                const countryBonus = COUNTRY_PRIORITY[location.countryCode] || 0
                score += countryBonus
            }

            // Deprioritize minor territories for short/generic queries
            // But still allow them to appear if the query is specific (5+ chars) or exact match
            if (isDeprioritized(location.name)) {
                const matchLength = normalizedQuery.length
                if (matchLength < 5 || (!locationName.startsWith(normalizedQuery) && locationName !== normalizedQuery)) {
                    score -= 50 // Heavy penalty for generic searches
                }
            }

            return { location, score }
        })
        .sort((a, b) => b.score - a.score) // Sort by score descending
        .slice(0, limit)
        .map(item => item.location)
}

/**
 * Determines the type of location and its country
 * Legacy function - now uses country-state-city package
 */
export function getLocationInfo(location: string): LocationInfo | null {
    const results = searchLocations(location, 1)
    return results.length > 0 ? results[0] : null
}

/**
 * Gets the country for a given location
 */
export function getLocationCountry(location: string): string {
    const info = getLocationInfo(location)
    return info?.country || location
}

/**
 * Checks if a location string matches a given type
 */
export function isLocationType(location: string, type: LocationType): boolean {
    const info = getLocationInfo(location)
    return info?.type === type
}

/**
 * Get all available locations for autocomplete
 * Returns a cached list of major cities and all countries
 * For better performance, we limit to major cities from popular countries
 */
export function getAllLocations(): LocationInfo[] {
    const locations: LocationInfo[] = []

    // Add all countries
    const countries = Country.getAllCountries()
    for (const country of countries) {
        locations.push({
            name: country.name,
            type: 'country',
            country: country.name,
            countryCode: country.isoCode,
            latitude: country.latitude,
            longitude: country.longitude
        })
    }

    // Add states (only for US and Canada)
    const stateCountries = ['US', 'CA']
    for (const countryCode of stateCountries) {
        const country = countries.find(c => c.isoCode === countryCode)
        const states = State.getStatesOfCountry(countryCode)
        for (const state of states) {
            locations.push({
                name: state.name,
                type: 'state',
                country: country?.name || countryCode,
                countryCode: countryCode,
                state: state.name,
                stateCode: state.isoCode,
                latitude: state.latitude || undefined,
                longitude: state.longitude || undefined
            })
        }
    }

    // Add all curated cities (already filtered by population tiers)
    for (const city of CURATED_CITIES) {
        const country = countries.find(c => c.isoCode === city.countryCode)

        // Only show state/province for US and Canada
        const shouldShowState = city.countryCode === 'US' || city.countryCode === 'CA'
        const state = shouldShowState && city.stateCode
            ? State.getStateByCodeAndCountry(city.stateCode, city.countryCode)
            : null

        locations.push({
            name: city.name,
            type: 'city',
            country: country?.name || city.countryCode,
            countryCode: city.countryCode,
            state: state?.name || undefined,
            stateCode: shouldShowState ? city.stateCode || undefined : undefined,
            latitude: city.latitude,
            longitude: city.longitude
        })
    }

    return locations
}
